<template>
  <SfModal
    v-if="modalOpen"
    class="period-selection-modal"
    :class="[
      modalClass,
      { 'period-selection-modal--footer-divider': showFooterDivider },
    ]"
    data-testid="period-selection-modal"
    size="m"
    @close="close"
  >
    <template #title
      >{{ I18n.t('companies_overview.period_selection_new.title') }}
    </template>

    <template #body>
      <SfVisibilityChecker
        data-testid="period-selection-body-top"
        @visibility-change="isBodyTopVisible = $event"
      />
      <SfInpageList data-testid="period-selection-options-list">
        <template
          v-for="(group, index) in groupedPeriodSelectionOptions"
          :key="group.category"
        >
          <div
            class="
              tw-bg-sf-snow-light
              tw-px-4
              tw-py-2
              tw-text-sf-slate-light-2
              tw-border-solid
              tw-border-sf-snow-dark
              tw-border-b
              tw-border-x-0
              tw-border-t-0
            "
            :class="{ 'tw-rounded-t-lg': index === 0 }"
          >
            {{
              I18n.t(
                `companies_overview.period_selection_new.categories.${group.category}`
              )
            }}
          </div>
          <PeriodSelectionItem
            v-for="periodSelectionOption in group.options"
            :key="periodSelectionOption.key"
            :period-selection-option="periodSelectionOption"
            :ledger-select-date="
              dateSelectionFor(periodSelectionOption.key).date
            "
            :ledger-select-lock-type="
              lockTypeSelectionFor(periodSelectionOption.key)
            "
            :ledger-select-date-type="
              dateSelectionFor(periodSelectionOption.key).dateType
            "
            :selected="periodSelection === periodSelectionOption.key"
            @item-click="changePeriodSelection(periodSelectionOption.key)"
            @date-type-selected="
              (dateType) =>
                handleDateTypeSelected(periodSelectionOption.key, dateType)
            "
            @lock-type-selected="
              (lockType) =>
                handleLockTypeSelected(periodSelectionOption.key, lockType)
            "
            @date-selected="
              (date) => handleDateSelected(periodSelectionOption.key, date)
            "
          />
        </template>
      </SfInpageList>

      <template v-if="description">
        <div
          v-dompurify-html="description"
          class="tw-text-center tw-text-sf-neutral-dark-1 tw-mt-4 tw-p-4"
        />
      </template>
      <SfVisibilityChecker
        data-testid="period-selection-body-bottom"
        @visibility-change="isBodyBottomVisible = $event"
      />
    </template>

    <template #footer>
      <SfButtonDs class="tw-mr-auto" @click="close">
        {{ I18n.t('common.cancel') }}
      </SfButtonDs>
      <SfButtonDs
        variant="primary"
        :disabled="!validForSave"
        class="rs-save-period-selection"
        @click="save"
      >
        {{ I18n.t('companies_overview.period_selection_new.apply_selection') }}
      </SfButtonDs>
    </template>
  </SfModal>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

import { SfModal } from '@app/shared/vue/components/sf-modal'
import { SfButtonDs } from '@silverfin/ui-components'
import { SfTime } from '@app/adapters/time'
import I18n from '@app/adapters/I18n'
import SfInpageList from '@app/shared/vue/components/sf-inpage-list/sf-inpage-list.vue'
import SfVisibilityChecker from '@app/shared/vue/components/sf-visibility-checker.vue'
import PeriodSelectionItem from './period-selection-item.vue'

import type {
  DateType,
  LockType,
  PeriodSelectionData,
  PeriodSelectionType,
} from '../types'
import {
  dateTypes,
  lockTypes,
  periodSelectionTypes,
  periodSelectionCategories,
} from '../types'
import { periodSelectionOptions } from '../constants'
import { lockTypeLabel } from '../utils/lock-type-label'
import { defaultPeriodDate } from '../utils/period-date'

const modalOpen = ref<boolean>(false)
const periodSelection = ref<PeriodSelectionType>(
  periodSelectionTypes.FINANCIAL_YEAR
)
type DateSelection = {
  dateType: DateType
  date: string | null
}

// Each option keeps its own date or lock type, so picking a value on one
// option leaves the others in its category unchanged.
const dateSelections = ref<Partial<Record<PeriodSelectionType, DateSelection>>>(
  {}
)

const defaultDateSelection = (key: PeriodSelectionType): DateSelection => {
  const date = defaultPeriodDate(key)
  return date
    ? { dateType: dateTypes.FIXED_DATE, date }
    : { dateType: dateTypes.TODAY, date: null }
}

const dateSelectionFor = (key: PeriodSelectionType): DateSelection =>
  dateSelections.value[key] ?? defaultDateSelection(key)

const ledgerSelectDateType = computed<DateType>(
  () => dateSelectionFor(periodSelection.value).dateType
)
const ledgerSelectDate = computed<string | null>(
  () => dateSelectionFor(periodSelection.value).date
)

const lockTypeSelections = ref<Partial<Record<PeriodSelectionType, LockType>>>(
  {}
)

const lockTypeSelectionFor = (key: PeriodSelectionType): LockType =>
  lockTypeSelections.value[key] ?? lockTypes.LOCKED

const ledgerSelectLockType = computed<LockType>(() =>
  lockTypeSelectionFor(periodSelection.value)
)
const saveCallback =
  ref<
    | ((data: {
        periodSelection: string | null
        ledgerSelectDate: string | null
        ledgerSelectLockType: LockType | undefined
      }) => void)
    | null
  >(null)

// Computed properties
const validForSave = computed<boolean>(() => {
  if (!periodSelection.value) {
    return false
  }
  return true
})

const currentDate = computed<string>(() => {
  return SfTime(new Date()).format('DD/MM/YYYY')
})

const modalClass = computed(() =>
  I18n.currentLocale().startsWith('en') ? '' : 'custom-size'
)

const selectedOption = computed(() =>
  periodSelectionOptions.find(
    (candidate) => candidate.key === periodSelection.value
  )
)

// Blank until the selected option defines a description; the missing key
// returns '' via defaultValue instead of a visible "[missing translation]"
// placeholder. By-end-date options vary the copy by date type (today vs a
// fixed end date); by-locking-state options have a single description
// parametrized by the resolved lock type instead.
const description = computed<string>(() => {
  const option = selectedOption.value
  if (!option) {
    return ''
  }

  if (option.category === periodSelectionCategories.BY_LOCKING_STATE) {
    return I18n.t(
      `companies_overview.period_selection_new.${option.key}_description`,
      {
        locking_state: lockTypeLabel(option.key, ledgerSelectLockType.value),
        defaultValue: '',
      }
    )
  }

  const isToday = ledgerSelectDateType.value === dateTypes.TODAY
  const suffix = isToday ? 'ending_today' : 'ending_date'

  return I18n.t(
    `companies_overview.period_selection_new.${option.key}_description_${suffix}`,
    {
      date: isToday ? currentDate.value : ledgerSelectDate.value,
      defaultValue: '',
    }
  )
})

// The modal body scrolls once its content is taller than the window. A marker
// out of view at either end of the content means part of it is scrolled away.
const isBodyTopVisible = ref(true)
const isBodyBottomVisible = ref(true)

const showFooterDivider = computed<boolean>(
  () =>
    !!description.value || !isBodyTopVisible.value || !isBodyBottomVisible.value
)

// Methods
// ModalCitizen interface
const openModal = (data: PeriodSelectionData): void => {
  // The markers are created again with the modal. Start from "fits" until they
  // report, so a value from the last opening does not stick.
  isBodyTopVisible.value = true
  isBodyBottomVisible.value = true
  periodSelection.value = data.periodSelection as PeriodSelectionType
  lockTypeSelections.value = {}
  if (data.ledgerSelectLockType) {
    lockTypeSelections.value[periodSelection.value] = data.ledgerSelectLockType
  }
  dateSelections.value = {}
  if (data.ledgerSelectDate) {
    dateSelections.value[periodSelection.value] = {
      dateType: dateTypes.FIXED_DATE,
      date: data.ledgerSelectDate,
    }
  }
  saveCallback.value = data.saveCallback
  modalOpen.value = true
}

const close = (): void => {
  modalOpen.value = false
}

const save = (): void => {
  if (saveCallback.value) {
    const isDateBased =
      selectedOption.value?.category === periodSelectionCategories.BY_END_DATE

    saveCallback.value({
      periodSelection: periodSelection.value,
      ledgerSelectDate:
        isDateBased && ledgerSelectDateType.value !== dateTypes.TODAY
          ? ledgerSelectDate.value
          : null,
      ledgerSelectLockType: isDateBased
        ? undefined
        : ledgerSelectLockType.value,
    })
  }
  close()
}

const groupedPeriodSelectionOptions = Object.values(
  periodSelectionCategories
).map((category) => ({
  category,
  options: periodSelectionOptions.filter(
    (option) => option.category === category
  ),
}))

const changePeriodSelection = (
  newPeriodSelection: PeriodSelectionType
): void => {
  periodSelection.value = newPeriodSelection
}

const handleLockTypeSelected = (
  key: PeriodSelectionType,
  lockType: LockType
): void => {
  lockTypeSelections.value[key] = lockType
}

const handleDateTypeSelected = (
  key: PeriodSelectionType,
  dateType: DateType
): void => {
  const { date } = dateSelectionFor(key)
  dateSelections.value[key] = {
    dateType,
    date: dateType === dateTypes.FIXED_DATE && !date ? currentDate.value : date,
  }
}

const handleDateSelected = (key: PeriodSelectionType, date: string): void => {
  dateSelections.value[key] = { ...dateSelectionFor(key), date }
}

// Expose methods for external access (ModalCitizen interface)
defineExpose({
  openModal,
})
</script>

<style lang="scss" scoped>
@import 'silverfin-ui-kit/src/scss/tooling';

.date-selector input::placeholder {
  @include color('text');
}

.sf-period-description {
  height: 40px;
}

.sf-period-name {
  font-size: 16px !important;
}

.sf-period-selector-card {
  border-radius: 8px;
  border: 1px solid var(--neutral-light-1, #cbcbcb);
  width: 265px;
}

.sf-period-selector-card__selected {
  border-color: #0aa1ba;
}

.date-selector-item__caret {
  position: absolute;
  left: auto;
  right: 100px;
}
</style>

<style lang="scss">
.period-selection-modal.custom-size .sf-modal-inner {
  width: 940px;
  max-width: 940px;
}

// The footer is rendered by SfModal. The extra .sf-modal outranks the UI kit's
// .sf-modal-white .sf-modal-footer padding.
.sf-modal.period-selection-modal--footer-divider .sf-modal-footer {
  @apply tw-pt-6;

  border-top: 1px solid theme('colors.sf-snow.dark');
}
</style>
